// Media Source Picker Web Component
// Allows users to browse and select files from OAuth-connected media sources
import { escapeHtml } from '../utils/html-utils.js';
import { openGoogleDrivePicker } from './google-drive-picker.js';
import { openGooglePhotosPicker } from './google-photos-picker.js';

class MediaSourcePicker extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isLoading = false;
    this.isLoadingMore = false;
    this.accounts = [];
    this.providers = [];
    this.currentAccount = null;
    this.currentPath = 'root';
    this.pathHistory = [];
    this.entries = [];
    this.selectedFiles = [];
    this.searchQuery = '';
    this.searchResults = [];
    this.isSearching = false;
    // Pagination state
    this.currentPage = 1;
    this.hasMore = false;
    this.nextPageToken = null;
    this.searchHasMore = false;
    this.searchNextPageToken = null;
    this.isOpeningPicker = false; // Guard to prevent multiple picker opens
    this.pendingPickerAccount = null; // Store account info for picker callbacks
    this.pendingPickerAccessToken = null; // Store access token for picker callbacks
  }

  isSingleSelect() {
    return this.getAttribute('selection-mode') === 'single';
  }

  isUrlOutput() {
    return this.getAttribute('output-mode') === 'url';
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    // Set up event delegation for connect account link (persists across re-renders)
    this._connectAccountHandler = (e) => {
      const connectLink = e.target.closest('#connectAccountLink');
      if (connectLink) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        // Small delay to allow modal to close, then navigate
        setTimeout(() => {
          window.location.href = '/settings/sparkle-sources';
        }, 100);
      }
    };
    this.addEventListener('click', this._connectAccountHandler);
  }

  disconnectedCallback() {
    // Clean up event listener
    if (this._connectAccountHandler) {
      this.removeEventListener('click', this._connectAccountHandler);
    }
  }

  render() {
    // Determine if current account uses picker or filesystem browser
    const usesPicker = this.isGoogleProvider(this.currentAccount?.provider);

    this.innerHTML = `
      <div class="media-picker-modal ${this.isOpen ? 'open' : ''}" id="mediaPickerModal">
        <div class="modal-backdrop" id="modalBackdrop"></div>
        <div class="modal-container">
          <div class="modal-header">
            <h3>✨ Share Sparkle from Elsewhere</h3>
            <button class="close-btn" id="closeBtn">×</button>
          </div>
          
          <div class="modal-body">
            ${this.renderAccountSelector()}
            ${usesPicker ? this.renderPickerMode() : this.renderFilesystemMode()}
          </div>
          
          ${!usesPicker ? `
          <div class="modal-footer">
            <div class="selected-count">${this.selectedFiles.length} selected</div>
            <div class="footer-actions">
              <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
              <button type="button" class="btn btn-primary" id="selectBtn" disabled>
                Select Files
              </button>
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    `;

    this.cacheDom();
    if (this.isOpen) {
      this.loadPickerData();
    } else {
      // Still attach event listeners even when closed (for close/cancel buttons)
      this.attachEventListeners();
    }
  }

  cacheDom() {
    this.backdrop = this.querySelector('#modalBackdrop');
    this.closeBtn = this.querySelector('#closeBtn');
    this.cancelBtn = this.querySelector('#cancelBtn');
    this.selectBtn = this.querySelector('#selectBtn');
    this.accountSelect = this.querySelector('#accountSelect');
    this.breadcrumb = this.querySelector('.breadcrumb');
    this.searchInput = this.querySelector('#searchInput');
    this.searchBtn = this.querySelector('#searchBtn');
    this.contentArea = this.querySelector('.picker-content');

    // Attach event listeners after caching DOM elements
    this.attachEventListeners();
  }

  /**
   * Check if provider uses Google Picker (instead of filesystem browser)
   */
  isGoogleProvider(provider) {
    return provider === 'google-drive' || provider === 'google-photos';
  }

  /**
   * Render picker mode UI (for Google providers)
   */
  renderPickerMode() {
    if (!this.currentAccount) {
      return '<div class="picker-loading"><p>Select an account to open picker</p></div>';
    }

    return `
      <div class="picker-loading">
        <div class="spinner"></div>
        <p>Opening ${this.currentAccount.providerDisplayName || this.currentAccount.provider} picker...</p>
      </div>
    `;
  }

  /**
   * Render filesystem browser mode UI (for non-Google providers)
   */
  renderFilesystemMode() {
    return `
      ${this.renderBreadcrumb()}
      ${this.renderSearch()}
      ${this.renderContent()}
    `;
  }

  renderAccountSelector() {
    if (this.accounts.length === 0 && !this.isLoading) {
      return `
        <div class="account-selector">
          <div class="empty-state">
            <p>No connected accounts yet.</p>
            <a href="/settings/sparkle-sources" class="btn btn-primary" id="connectAccountLink">Connect an Account</a>
          </div>
        </div>
      `;
    }

    // Determine which account should be selected
    const selectedAccountId = this.currentAccount?.id || '';

    return `
      <div class="account-selector">
        <label for="accountSelect">Account:</label>
        <select id="accountSelect" class="form-control">
          <option value="">Select an account...</option>
          ${this.accounts.map(account => `
            <option value="${account.id}" 
                    data-provider="${account.provider}" 
                    data-account-id="${account.accountIdentifier}"
                    ${account.id === selectedAccountId ? 'selected' : ''}>
              ${account.providerDisplayName} - ${account.accountLabel}
              ${account.isExpired ? ' (Expired)' : ''}
            </option>
          `).join('')}
        </select>
      </div>
    `;
  }

  renderBreadcrumb() {
    if (!this.currentAccount) {
      return '<div class="breadcrumb"></div>';
    }

    const canGoBack = this.pathHistory.length > 0;
    const breadcrumbItems = [
      { name: 'Home', path: 'root' },
      ...this.pathHistory.map((item) => ({
        name: item.name,
        path: item.path
      }))
    ];

    return `
      <div class="breadcrumb">
        ${canGoBack ? `
          <button type="button" class="back-btn" id="backBtn" title="Go back">
            ← Back
          </button>
        ` : ''}
        <div class="breadcrumb-items">
          ${breadcrumbItems.map((item, idx) => `
            <button type="button" class="breadcrumb-item" data-path="${item.path}">
              ${escapeHtml(item.name)}
            </button>
            ${idx < breadcrumbItems.length - 1 ? '<span class="breadcrumb-separator">/</span>' : ''}
          `).join('')}
        </div>
      </div>
    `;
  }

  renderSearch() {
    if (!this.currentAccount) {
      return '<div class="search-bar" style="display: none;"></div>';
    }

    return `
      <div class="search-bar">
        <input 
          type="search" 
          id="searchInput" 
          class="form-control" 
          placeholder="Search files..."
          value="${this.searchQuery}"
        />
        <button type="button" id="searchBtn" class="btn btn-secondary">🔍 Search</button>
        ${this.searchQuery ? '<button type="button" id="clearSearchBtn" class="btn btn-text">Clear</button>' : ''}
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="picker-content">
          <div class="loading-state">
            <div class="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      `;
    }

    if (!this.currentAccount) {
      return `
        <div class="picker-content">
          <div class="empty-state">
            <p>Select an account to browse files</p>
          </div>
        </div>
      `;
    }

    if (this.isSearching) {
      return this.renderSearchResults();
    }

    if (this.entries.length === 0) {
      return `
        <div class="picker-content">
          <div class="empty-state">
            <p>This folder is empty</p>
          </div>
        </div>
      `;
    }

    return `
      <div class="picker-content">
        <div class="file-grid">
          ${this.entries.map(entry => this.renderEntry(entry)).join('')}
        </div>
        ${this.hasMore ? `
          <div class="pagination-controls">
            <button type="button" class="btn btn-secondary" id="loadMoreBtn" ${this.isLoadingMore ? 'disabled' : ''}>
              ${this.isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  renderEntry(entry) {
    const isSelected = this.selectedFiles.some(f =>
      f.provider === this.currentAccount.provider &&
      f.accountId === this.currentAccount.accountIdentifier &&
      f.fileId === entry.id
    );

    const icon = entry.type === 'directory' ? '📁' : this.getFileIcon(entry.contentType);

    // Use thumbnail if available, otherwise use icon
    const thumbnailHtml = entry.thumbnailUrl
      ? `<img src="${escapeHtml(entry.thumbnailUrl)}" alt="${escapeHtml(entry.name)}" class="file-thumbnail" loading="lazy" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />`
      : '';
    const iconHtml = entry.thumbnailUrl
      ? `<div class="file-icon" style="display: none;">${icon}</div>`
      : `<div class="file-icon">${icon}</div>`;

    return `
      <div class="file-item ${entry.type === 'directory' ? 'directory' : 'file'} ${isSelected ? 'selected' : ''}" 
           data-entry-id="${entry.id}" 
           data-entry-type="${entry.type}">
        <div class="file-checkbox">
          <input type="checkbox" ${isSelected ? 'checked' : ''} />
        </div>
        ${thumbnailHtml}
        ${iconHtml}
        <div class="file-info">
          <div class="file-name" title="${entry.name}">${entry.name}</div>
          ${entry.size ? `<div class="file-size">${this.formatFileSize(entry.size)}</div>` : ''}
        </div>
      </div>
    `;
  }

  renderSearchResults() {
    if (this.searchResults.length === 0 && !this.isLoading) {
      return `
        <div class="picker-content">
          <div class="empty-state">
            <p>No files found matching "${this.searchQuery}"</p>
          </div>
        </div>
      `;
    }

    return `
      <div class="picker-content">
        <div class="file-grid">
          ${this.searchResults.map(entry => this.renderEntry(entry)).join('')}
        </div>
        ${this.searchHasMore ? `
          <div class="pagination-controls">
            <button type="button" class="btn btn-secondary" id="loadMoreSearchBtn" ${this.isLoadingMore ? 'disabled' : ''}>
              ${this.isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  getFileIcon(contentType) {
    if (!contentType) return '📄';

    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.startsWith('video/')) return '🎥';
    if (contentType.startsWith('audio/')) return '🎵';
    if (contentType.includes('pdf')) return '📕';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('spreadsheet') || contentType.includes('excel')) return '📊';

    return '📄';
  }

  formatFileSize(bytes) {
    if (!bytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  attachEventListeners() {
    // Remove existing listeners to prevent duplicates
    if (this._backdropHandler) {
      this.backdrop?.removeEventListener('click', this._backdropHandler);
    }
    if (this._closeBtnHandler) {
      this.closeBtn?.removeEventListener('click', this._closeBtnHandler);
    }
    if (this._cancelBtnHandler) {
      this.cancelBtn?.removeEventListener('click', this._cancelBtnHandler);
    }
    if (this._selectBtnHandler) {
      this.selectBtn?.removeEventListener('click', this._selectBtnHandler);
    }
    if (this._accountSelectHandler) {
      this.accountSelect?.removeEventListener('change', this._accountSelectHandler);
    }
    if (this._searchBtnHandler) {
      this.searchBtn?.removeEventListener('click', this._searchBtnHandler);
    }
    if (this._searchInputHandler) {
      this.searchInput?.removeEventListener('keypress', this._searchInputHandler);
    }
    if (this._clearSearchBtnHandler) {
      const clearSearchBtn = this.querySelector('#clearSearchBtn');
      clearSearchBtn?.removeEventListener('click', this._clearSearchBtnHandler);
    }

    // Create new handler functions and store references
    this._backdropHandler = () => this.close();
    this._closeBtnHandler = () => this.close();
    this._cancelBtnHandler = () => this.close();
    this._selectBtnHandler = () => this.handleSelect();
    this._accountSelectHandler = (e) => this.handleAccountChange(e);
    this._searchBtnHandler = () => this.handleSearch();
    this._searchInputHandler = (e) => {
      if (e.key === 'Enter') {
        this.handleSearch();
      }
    };
    this._clearSearchBtnHandler = () => this.clearSearch();

    // Attach new listeners
    this.backdrop?.addEventListener('click', this._backdropHandler);
    this.closeBtn?.addEventListener('click', this._closeBtnHandler);
    this.cancelBtn?.addEventListener('click', this._cancelBtnHandler);
    this.selectBtn?.addEventListener('click', this._selectBtnHandler);
    this.accountSelect?.addEventListener('change', this._accountSelectHandler);
    this.searchBtn?.addEventListener('click', this._searchBtnHandler);
    this.searchInput?.addEventListener('keypress', this._searchInputHandler);

    const clearSearchBtn = this.querySelector('#clearSearchBtn');
    clearSearchBtn?.addEventListener('click', this._clearSearchBtnHandler);

    // Load More button handlers
    const loadMoreBtn = this.querySelector('#loadMoreBtn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => this.loadMoreDirectory());
    }

    const loadMoreSearchBtn = this.querySelector('#loadMoreSearchBtn');
    if (loadMoreSearchBtn) {
      loadMoreSearchBtn.addEventListener('click', () => this.loadMoreSearch());
    }

    // Delegate events for file items
    this.contentArea?.addEventListener('click', (e) => {
      const fileItem = e.target.closest('.file-item');
      if (!fileItem) return;

      const entryId = fileItem.dataset.entryId;
      const entryType = fileItem.dataset.entryType;

      if (e.target.type === 'checkbox' || e.target.closest('.file-checkbox')) {
        this.toggleFileSelection(entryId, entryType, fileItem);
      } else if (entryType === 'directory') {
        this.navigateToDirectory(entryId);
      }
    });

    // Back button handler
    const backBtn = this.querySelector('#backBtn');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.goBack();
      });
    }

    // Breadcrumb navigation
    this.breadcrumb?.addEventListener('click', (e) => {
      const breadcrumbItem = e.target.closest('.breadcrumb-item');
      if (breadcrumbItem) {
        const path = breadcrumbItem.dataset.path;
        this.navigateToPath(path);
      }
    });
  }

  async loadPickerData() {
    this.isLoading = true;
    this.updateContent();

    try {
      const response = await fetch('/api/media-sources/picker', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load picker data');
      }

      const data = await response.json();
      this.accounts = data.accounts || [];
      this.providers = data.providers || [];

      this.isLoading = false;
      this.updateContent();
    } catch (error) {
      window.logger.error('Error loading picker data:', error);
      this.isLoading = false;
      this.updateContent();
      this.showError('Failed to load accounts. Please try again.');
    }
  }

  async handleAccountChange(e) {
    const option = e.target.selectedOptions[0];
    if (!option || !option.value) {
      this.currentAccount = null;
      this.currentPath = 'root';
      this.pathHistory = [];
      this.entries = [];
      this.updateContent();
      return;
    }

    this.currentAccount = {
      id: option.value,
      provider: option.dataset.provider,
      accountIdentifier: option.dataset.accountId,
      providerDisplayName: this.accounts.find(a => a.id === option.value)?.providerDisplayName
    };

    // Check if this is a Google provider
    if (this.isGoogleProvider(this.currentAccount.provider)) {
      // Prevent multiple simultaneous picker opens
      if (this.isOpeningPicker) {
        return;
      }

      // For Google providers, close the modal first, then open picker
      const provider = this.currentAccount.provider;
      const accountIdentifier = this.currentAccount.accountIdentifier;
      // Store account info for picker callbacks (since close() clears currentAccount)
      this.pendingPickerAccount = {
        provider: this.currentAccount.provider,
        accountIdentifier: this.currentAccount.accountIdentifier,
        id: this.currentAccount.id,
        providerDisplayName: this.currentAccount.providerDisplayName
      };
      this.isOpeningPicker = true;
      this.close();

      // Small delay to allow modal to close before opening Google picker
      setTimeout(async () => {
        try {
          // Open appropriate picker
          if (provider === 'google-drive') {
            await openGoogleDrivePicker(this, provider, accountIdentifier);
          } else if (provider === 'google-photos') {
            await openGooglePhotosPicker(this, provider, accountIdentifier);
          }
        } catch (error) {
          // Error already handled in openGoogleDrivePicker/openGooglePhotosPicker
          // Guard will be reset there
          window.logger.debug('Error opening picker:', { error });
        }
      }, 100);
    } else {
      // For other providers, use filesystem browser
      this.currentPath = 'root';
      this.pathHistory = [];
      this.selectedFiles = [];
      this.updateSelectButton();
      await this.loadDirectory('root');
    }
  }

  async loadDirectory(path, append = false) {
    if (!this.currentAccount) return;

    // If not appending, reset pagination state
    if (!append) {
      this.isLoading = true;
      this.currentPage = 1;
      this.hasMore = false;
      this.nextPageToken = null;
      this.entries = [];
    } else {
      this.isLoadingMore = true;
    }
    this.updateContent();

    try {
      const url = new URL(
        `/api/media-sources/${this.currentAccount.provider}/${this.currentAccount.accountIdentifier}/list`,
        window.location.origin
      );
      url.searchParams.set('path', path);
      url.searchParams.set('limit', '50');

      // When appending (loading more), use pageToken if available
      // Otherwise, use page number for initial load
      if (append && this.nextPageToken) {
        url.searchParams.set('pageToken', this.nextPageToken);
      } else if (!append) {
        url.searchParams.set('page', this.currentPage.toString());
      }

      const response = await fetch(url.toString(), { credentials: 'include' });

      if (!response.ok) {
        if (response.status === 403) {
          const data = await response.json();
          if (data.requiresReconnect) {
            this.showError('Your connection has expired. Please reconnect your account.');
            return;
          }
        }
        throw new Error('Failed to load directory');
      }

      const data = await response.json();

      if (append) {
        // Append new entries to existing ones
        this.entries = [...this.entries, ...(data.entries || [])];
      } else {
        // Replace entries
        this.entries = data.entries || [];
      }

      this.currentPath = path;
      this.hasMore = data.hasMore || false;
      this.nextPageToken = data.nextPageToken || null;
      this.isSearching = false;
      this.searchQuery = '';

      this.isLoading = false;
      this.isLoadingMore = false;
      this.updateContent();
    } catch (error) {
      window.logger.error('Error loading directory:', error);
      this.isLoading = false;
      this.isLoadingMore = false;
      this.updateContent();
      this.showError('Failed to load directory. Please try again.');
    }
  }

  async loadMoreDirectory() {
    if (!this.hasMore || this.isLoadingMore || !this.nextPageToken) return;

    // Don't increment currentPage - use nextPageToken instead
    await this.loadDirectory(this.currentPath, true);
  }

  async navigateToDirectory(entryId) {
    const entry = this.entries.find(e => e.id === entryId);
    if (!entry || entry.type !== 'directory') return;

    this.pathHistory.push({
      name: entry.name,
      path: this.currentPath
    });

    await this.loadDirectory(entry.path || entryId);
  }

  navigateToPath(path) {
    if (path === 'root') {
      this.pathHistory = [];
      this.loadDirectory('root');
    } else {
      const pathIndex = this.pathHistory.findIndex(item => item.path === path);
      if (pathIndex >= 0) {
        this.pathHistory = this.pathHistory.slice(0, pathIndex + 1);
        this.loadDirectory(path);
      }
    }
  }

  goBack() {
    if (this.pathHistory.length === 0) {
      // Already at root, go to root anyway
      this.pathHistory = [];
      this.loadDirectory('root');
      return;
    }

    // Remove the last item from history and navigate to the previous path
    this.pathHistory.pop();

    // Navigate to the previous path (or root if no history left)
    const targetPath = this.pathHistory.length > 0
      ? this.pathHistory[this.pathHistory.length - 1].path
      : 'root';

    if (targetPath === 'root') {
      this.pathHistory = [];
    }

    this.loadDirectory(targetPath);
  }

  async handleSearch(append = false) {
    if (!this.currentAccount || !this.searchInput?.value.trim()) {
      return;
    }

    const query = this.searchInput.value.trim();

    // If not appending, reset search state
    if (!append) {
      this.searchQuery = query;
      this.isSearching = true;
      this.isLoading = true;
      this.searchResults = [];
      this.searchHasMore = false;
      this.searchNextPageToken = null;
    } else {
      this.isLoadingMore = true;
    }
    this.updateContent();

    try {
      const requestBody = {
        query: this.searchQuery,
        maxResults: 50
      };

      // Add pagination token if loading more
      if (append && this.searchNextPageToken) {
        requestBody.pageToken = this.searchNextPageToken;
      }

      const response = await fetch(
        `/api/media-sources/${this.currentAccount.provider}/${this.currentAccount.accountIdentifier}/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      if (append) {
        // Append new results to existing ones
        this.searchResults = [...this.searchResults, ...(data.results || [])];
      } else {
        // Replace results
        this.searchResults = data.results || [];
      }

      this.searchHasMore = data.hasMore || false;
      this.searchNextPageToken = data.nextPageToken || null;

      this.isLoading = false;
      this.isLoadingMore = false;
      this.updateContent();
    } catch (error) {
      window.logger.error('Error searching:', error);
      this.isLoading = false;
      this.isLoadingMore = false;
      this.updateContent();
      this.showError('Search failed. Please try again.');
    }
  }

  async loadMoreSearch() {
    if (!this.searchHasMore || this.isLoadingMore || !this.searchNextPageToken) return;
    await this.handleSearch(true);
  }

  clearSearch() {
    this.searchQuery = '';
    this.isSearching = false;
    this.searchResults = [];
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.updateContent();
    if (this.currentAccount) {
      this.loadDirectory(this.currentPath);
    }
  }

  toggleFileSelection(entryId, entryType, fileItem) {
    if (entryType === 'directory') return;

    const entry = (this.isSearching ? this.searchResults : this.entries).find(e => e.id === entryId);
    if (!entry) return;

    const index = this.selectedFiles.findIndex(f =>
      f.provider === this.currentAccount.provider &&
      f.accountId === this.currentAccount.accountIdentifier &&
      f.fileId === entryId
    );

    if (index >= 0) {
      this.selectedFiles.splice(index, 1);
      fileItem.classList.remove('selected');
    } else {
      if (this.isSingleSelect()) {
        this.selectedFiles = [];
        this.querySelectorAll('.file-item.selected').forEach((el) => el.classList.remove('selected'));
      }
      this.selectedFiles.push({
        provider: this.currentAccount.provider,
        accountId: this.currentAccount.accountIdentifier,
        fileId: entryId,
        fileUrl: entry.url || `oauth://${this.currentAccount.provider}/${this.currentAccount.accountIdentifier}/${entryId}`,
        fileName: entry.name,
        mimeType: entry.mimeType || entry.mime_type || null
      });
      fileItem.classList.add('selected');
    }

    this.updateSelectButton();
  }

  updateSelectButton() {
    if (this.selectBtn) {
      this.selectBtn.disabled = this.selectedFiles.length === 0;
      if (this.isUrlOutput()) {
        this.selectBtn.textContent = this.selectedFiles.length === 0 ? 'Use Image' : 'Use Selected Image';
      } else {
        this.selectBtn.textContent =
          this.selectedFiles.length === 0
            ? 'Select Files'
            : `Select ${this.selectedFiles.length} File${this.selectedFiles.length !== 1 ? 's' : ''}`;
      }
    }

    const selectedCount = this.querySelector('.selected-count');
    if (selectedCount) {
      selectedCount.textContent = `${this.selectedFiles.length} selected`;
    }
  }

  async handleSelect() {
    if (this.selectedFiles.length === 0) return;

    try {
      const response = await fetch('/api/media-sources/picker/select', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          files: this.selectedFiles
        })
      });

      if (!response.ok) {
        throw new Error('Failed to select files');
      }

      const data = await response.json();

      if (this.isUrlOutput()) {
        const file = (data.files || this.selectedFiles)[0];
        if (!file) {
          throw new Error('No file selected');
        }
        this.dispatchEvent(new CustomEvent('image-url-selected', {
          detail: { fileUrl: file.fileUrl || file.url },
          bubbles: true
        }));
        this.close();
        return;
      }

      // Dispatch event with selected files and textarea reference
      this.dispatchEvent(new CustomEvent('files-selected', {
        detail: {
          files: data.files,
          textarea: this._targetTextarea || null
        },
        bubbles: true
      }));

      this.close();
    } catch (error) {
      window.logger.error('Error selecting files:', error);
      this.showError('Failed to select files. Please try again.');
    }
  }

  showError(message) {
    // Simple error display - could be enhanced
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    this.querySelector('.modal-body').prepend(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  updateContent() {
    // Update specific parts of the UI without full re-render
    const accountSelector = this.querySelector('.account-selector');
    if (accountSelector) {
      // Remove old event listener if it exists
      if (this.accountSelect && this._accountSelectHandler) {
        this.accountSelect.removeEventListener('change', this._accountSelectHandler);
      }

      accountSelector.outerHTML = this.renderAccountSelector();
      this.accountSelect = this.querySelector('#accountSelect');

      // Set the selected value explicitly to ensure it's preserved
      // Do this BEFORE attaching the event listener to prevent triggering change event
      if (this.accountSelect && this.currentAccount?.id) {
        this.accountSelect.value = this.currentAccount.id;
      }

      // Reattach event listener AFTER setting value to prevent triggering on programmatic change
      this._accountSelectHandler = (e) => this.handleAccountChange(e);
      this.accountSelect?.addEventListener('change', this._accountSelectHandler);
    }

    const breadcrumb = this.querySelector('.breadcrumb');
    if (breadcrumb) {
      breadcrumb.outerHTML = this.renderBreadcrumb();
      this.breadcrumb = this.querySelector('.breadcrumb');

      // Back button handler
      const backBtn = this.querySelector('#backBtn');
      if (backBtn) {
        backBtn.addEventListener('click', () => {
          this.goBack();
        });
      }

      // Breadcrumb click handler
      this.breadcrumb?.addEventListener('click', (e) => {
        const breadcrumbItem = e.target.closest('.breadcrumb-item');
        if (breadcrumbItem) {
          const path = breadcrumbItem.dataset.path;
          this.navigateToPath(path);
        }
      });
    }

    const searchBar = this.querySelector('.search-bar');
    if (searchBar) {
      searchBar.outerHTML = this.renderSearch();
      this.searchInput = this.querySelector('#searchInput');
      this.searchBtn = this.querySelector('#searchBtn');
      this.searchBtn?.addEventListener('click', () => this.handleSearch());
      this.searchInput?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSearch();
        }
      });
      const clearSearchBtn = this.querySelector('#clearSearchBtn');
      clearSearchBtn?.addEventListener('click', () => this.clearSearch());
    }

    const contentArea = this.querySelector('.picker-content');
    if (contentArea) {
      contentArea.outerHTML = this.renderContent();
      this.contentArea = this.querySelector('.picker-content');

      // Re-attach file item listeners
      this.contentArea?.addEventListener('click', (e) => {
        const fileItem = e.target.closest('.file-item');
        if (!fileItem) return;

        const entryId = fileItem.dataset.entryId;
        const entryType = fileItem.dataset.entryType;

        if (e.target.type === 'checkbox' || e.target.closest('.file-checkbox')) {
          this.toggleFileSelection(entryId, entryType, fileItem);
        } else if (entryType === 'directory') {
          this.navigateToDirectory(entryId);
        }
      });

      // Re-attach Load More button handlers
      const loadMoreBtn = this.querySelector('#loadMoreBtn');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', () => this.loadMoreDirectory());
      }

      const loadMoreSearchBtn = this.querySelector('#loadMoreSearchBtn');
      if (loadMoreSearchBtn) {
        loadMoreSearchBtn.addEventListener('click', () => this.loadMoreSearch());
      }
    }

    this.updateSelectButton();
  }

  open() {
    this.isOpen = true;
    this.render();
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.isOpen = false;
    this.render();
    document.body.style.overflow = '';
    this.selectedFiles = [];
    this.currentAccount = null;
    this.currentPath = 'root';
    this.pathHistory = [];
    this.entries = [];
    this.searchQuery = '';
    this.searchResults = [];
    this.isSearching = false;
    // Reset pagination state
    this.currentPage = 1;
    this.hasMore = false;
    this.nextPageToken = null;
    this.searchHasMore = false;
    this.searchNextPageToken = null;
  }

  // Google Drive and Google Photos picker methods are now in separate modules:
  // - google-drive-picker.js (openGoogleDrivePicker, handlePickerCallback, findOrCreateAppFolder, copyFileToAppFolder)
  // - google-photos-picker.js (openGooglePhotosPicker, handlePhotoPickerCallback)
  // These methods are imported and called via the imported functions above.

}
customElements.define('media-source-picker', MediaSourcePicker);

export default MediaSourcePicker;

